import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, Eye, EyeOff, CheckCircle2, XCircle, RefreshCw } from 'lucide-react';
import axios from 'axios';

export default function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [backendStatus, setBackendStatus] = useState('checking'); // checking, connected, error
  const [backendVersion, setBackendVersion] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();

  const checkBackend = async () => {
    setBackendStatus('checking');
    try {
      const res = await axios.get('/api/health', { timeout: 10000 });
      setBackendVersion(res.data?.version || 'unknown');
      setBackendStatus('connected');
    } catch (err) {
      console.error('[Login] Backend health check failed:', err.message);
      setBackendStatus('error');
    }
  };

  useEffect(() => {
    // Clear stale auth data when landing on login page
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    checkBackend();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (backendStatus === 'error') {
      toast.error('Backend is not reachable. Please check deployment.');
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success('Login successful!');
      navigate('/dashboard');
    } catch (error) {
      const msg = error.code === 'VERSION_MISMATCH'
        ? 'Backend needs redeployment (old version detected).'
        : error.code === 'INVALID_RESPONSE'
        ? 'Backend returned an unexpected response.'
        : error.response?.data?.detail || error.message || 'Invalid credentials';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <div className="absolute inset-0 gradient-primary opacity-50" />
      <Card className="w-full max-w-md relative z-10 border-border/50 shadow-xl" data-testid="login-card">
        <CardHeader className="text-center space-y-2">
          <div className="mx-auto w-12 h-12 rounded-xl bg-primary flex items-center justify-center mb-2">
            <span className="text-primary-foreground font-heading font-bold text-2xl">F</span>
          </div>
          <CardTitle className="font-heading text-2xl">Welcome to FinNexus</CardTitle>
          <CardDescription>Sign in to your account</CardDescription>
        </CardHeader>
        <CardContent>
          {/* Backend Status Indicator */}
          <div className="mb-4 flex items-center justify-center gap-2 text-xs" data-testid="backend-status">
            {backendStatus === 'checking' && (
              <span className="flex items-center gap-1.5 text-yellow-500">
                <Loader2 className="w-3 h-3 animate-spin" /> Checking backend...
              </span>
            )}
            {backendStatus === 'connected' && (
              <span className="flex items-center gap-1.5 text-green-500">
                <CheckCircle2 className="w-3 h-3" /> Backend connected {backendVersion && `(v${backendVersion})`}
              </span>
            )}
            {backendStatus === 'error' && (
              <span className="flex items-center gap-1.5 text-red-500">
                <XCircle className="w-3 h-3" /> Backend not reachable
                <button onClick={checkBackend} className="ml-1 hover:text-red-400">
                  <RefreshCw className="w-3 h-3" />
                </button>
              </span>
            )}
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" placeholder="example@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required data-testid="login-email-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="password">Password</Label>
              <div className="relative">
                <Input id="password" type={showPassword ? 'text' : 'password'} placeholder="--------" value={password} onChange={(e) => setPassword(e.target.value)} required data-testid="login-password-input" />
                <Button type="button" variant="ghost" size="icon" className="absolute right-0 top-0 h-full px-3 hover:bg-transparent" onClick={() => setShowPassword(!showPassword)}>
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </Button>
              </div>
            </div>
            <Button type="submit" className="w-full rounded-full" disabled={loading || backendStatus === 'error'} data-testid="login-submit-btn">
              {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}Sign In
            </Button>
          </form>
          <div className="mt-6 text-center text-sm">
            <span className="text-muted-foreground">Contact your admin for access</span>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
